import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { SINE_META, type SineParams } from './sine.model';

/** Sine params with index signature for JsxParamStore compatibility */
type SineParamsIndexed = SineParams & { [key: string]: number };

/**
 * Creates sine functions: a * sin(b(x - c)) + d
 *
 * Parameters:
 * - a: amplitude
 * - b: frequency (affects period = 2π/b)
 * - c: phase shift (horizontal translation)
 * - d: vertical shift
 */
export class SineCreator extends BaseFunctionCreator<SineParamsIndexed> {
  readonly functionType = 'sine';
  readonly defaultParams: SineParamsIndexed = { ...SINE_META.defaultParams };
  readonly availableFeatures = SINE_META.availableFeatures;
  readonly handleConfigs = SINE_META.handleConfigs;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as SineParamsIndexed;

    // Get stroke color for handles (needs to match curve color)
    const colorName = (display?.curve?.color ?? 'pink') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: SINE_META.defaultRanges.a },
      b: { value: initialParams.b, range: SINE_META.defaultRanges.b },
      c: { value: initialParams.c, range: SINE_META.defaultRanges.c },
      d: { value: initialParams.d, range: SINE_META.defaultRanges.d },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const b = paramStore.getValue('b');
      const c = paramStore.getValue('c');
      const d = paramStore.getValue('d');
      return a * Math.sin(b * (x - c)) + d;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    const getDerivative = (order = 1): ((x: number) => number) => {
      // Order 1 derivative: a * b * cos(b(x - c))
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          return a * b * Math.cos(b * (x - c));
        };
      }

      // Order 2 derivative: -a * b * b * sin(b(x - c))
      if (order === 2) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          return -a * b * b * Math.sin(b * (x - c));
        };
      }

      // Order 3 derivative: -a * b * b * b * cos(b(x - c))
      if (order === 3) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          return -a * Math.pow(b, 3) * Math.cos(b * (x - c));
        };
      }

      // Order 4 derivative: a * b * b * b * b * sin(b(x - c))
      if (order === 4) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          return a * Math.pow(b, 4) * Math.sin(b * (x - c));
        };
      }
      // For higher orders, cycle back (sin derivatives repeat every 4)
      return getDerivative(((order - 1) % 4) + 1);
    };

    return {
      id,
      type: 'sine',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as SineParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
